import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './JobLogsOverview.module.css';
import { jobApi, JobRun } from '../../services/jobApi';

const JobLogsOverview: React.FC = () => {
  const [jobRuns, setJobRuns] = useState<JobRun[]>([]);
  const [filteredRuns, setFilteredRuns] = useState<JobRun[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    loadJobRuns();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    filterJobRuns();
  }, [jobRuns, statusFilter, searchTerm]);

  const loadJobRuns = async () => {
    try {
      setLoading(true);
      const data = await jobApi.getAllJobRuns();
      if (isMountedRef.current) {
        setJobRuns(data);
      }
    } catch (error) {
      console.error('Error loading job runs:', error);
      toast.error('Failed to load job runs. Please try again later.', {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const filterJobRuns = () => {
    let filtered = jobRuns;

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter(run => run.status === statusFilter);
    }

    // Filter by search term (in error message)
    if (searchTerm) {
      filtered = filtered.filter(run => 
        run.error_message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        run.job_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredRuns(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return '-';
    return `${duration.toFixed(2)}s`;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toUpperCase()) {
      case 'SUCCESS':
        return 'bg-success';
      case 'FAILED':
        return 'bg-danger';
      case 'RUNNING':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  };

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading job runs...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">Job Log Management</li>
          </ol>
        </nav>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">All Job Runs</h2>
            <p className="text-muted mb-0">
              <i className="bi bi-clock-history me-2"></i>
              Monitor and review job execution history
            </p>
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={loadJobRuns}
              title="Refresh job runs"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filtersContainer}>
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bi bi-funnel me-2"></i>
                Filter Options
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <label htmlFor="statusFilter" className="form-label">
                    <i className="bi bi-flag me-1"></i>
                    Filter by status:
                  </label>
                  <select 
                    id="statusFilter" 
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="FAILED">❌ Failed</option>
                    <option value="SUCCESS">✅ Success</option>
                    <option value="RUNNING">🔄 Running</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label htmlFor="searchInput" className="form-label">
                    <i className="bi bi-search me-1"></i>
                    Search messages:
                  </label>
                  <input 
                    type="text" 
                    id="searchInput" 
                    className="form-control" 
                    placeholder="Search in error messages or job IDs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="w-100">
                    <label className="form-label">Quick Actions:</label>
                    <div className="d-flex gap-2">
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {setStatusFilter('FAILED'); setSearchTerm('');}}
                        title="Show only failed jobs"
                      >
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Failed Only
                      </button>
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {setStatusFilter(''); setSearchTerm('');}}
                        title="Clear all filters"
                      >
                        <i className="bi bi-x-circle me-1"></i>
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
            <div className="d-flex align-items-center">
              <i className="bi bi-table me-2 text-primary"></i>
              <h5 className="mb-0">Job Execution History</h5>
            </div>
            <div className="d-flex align-items-center gap-3">
              <span className="badge bg-primary rounded-pill">
                {filteredRuns.length} of {jobRuns.length} runs
              </span>
              {filteredRuns.length !== jobRuns.length && (
                <small className="text-muted">
                  <i className="bi bi-funnel-fill me-1"></i>
                  Filtered
                </small>
              )}
            </div>
          </div>
          
          <table className={`table ${styles.jobLogsTable}`}>
            <thead>
              <tr>
                <th>
                  <i className="bi bi-hash me-1"></i>
                  ID
                </th>
                <th>
                  <i className="bi bi-gear me-1"></i>
                  Job ID
                </th>
                <th>
                  <i className="bi bi-calendar-event me-1"></i>
                  Scheduled
                </th>
                <th>
                  <i className="bi bi-play-circle me-1"></i>
                  Start
                </th>
                <th>
                  <i className="bi bi-stop-circle me-1"></i>
                  End
                </th>
                <th>
                  <i className="bi bi-flag me-1"></i>
                  Status
                </th>
                <th>
                  <i className="bi bi-stopwatch me-1"></i>
                  Duration
                </th>
                <th>
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Error
                </th>
                <th>
                  <i className="bi bi-eye me-1"></i>
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRuns.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center p-5">
                    <div className={styles.emptyState}>
                      {jobRuns.length === 0 ? (
                        <>
                          <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                          <h5 className="text-muted">No job runs found</h5>
                          <p className="text-muted mb-0">
                            No job executions have been recorded yet.
                          </p>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search display-4 text-muted mb-3"></i>
                          <h5 className="text-muted">No matching results</h5>
                          <p className="text-muted mb-0">
                            Try adjusting your filters to see more results.
                          </p>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRuns.map((run, index) => (
                  <tr key={run.id} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                    <td className={styles.jobLogsTableCell}>
                      <span className={styles.idBadge}>#{run.id}</span>
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      <span className={styles.jobId}>{run.job_id}</span>
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      <span className={styles.timestamp}>{formatDate(run.scheduled_time)}</span>
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      <span className={styles.timestamp}>{formatDate(run.start_time)}</span>
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      {run.end_time ? (
                        <span className={styles.timestamp}>{formatDate(run.end_time)}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      <span className={`badge ${getStatusBadgeClass(run.status)} ${styles.statusBadge}`}>
                        {run.status === 'SUCCESS' && '✅ '}
                        {run.status === 'FAILED' && '❌ '}
                        {run.status === 'RUNNING' && '🔄 '}
                        {run.status}
                      </span>
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      <span className={styles.duration}>{formatDuration(run.duration)}</span>
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      {run.error_message ? (
                        <span 
                          className={styles.errorMessage}
                          title={run.error_message}
                        >
                          <i className="bi bi-exclamation-circle me-1"></i>
                          {run.error_message.length > 30 
                            ? `${run.error_message.substring(0, 30)}...` 
                            : run.error_message
                          }
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className={styles.jobLogsTableCell}>
                      <Link 
                        to={`/admin/job-logs/detail?run_id=${run.id}`}
                        className="btn btn-sm btn-outline-info"
                        title="View detailed logs"
                      >
                        <i className="bi bi-eye"></i>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
        limit={3}
      />
    </div>
  );
};

export default JobLogsOverview;
